import React from 'react';
import { Edit2, Calendar, User, Building2, Mail, Phone } from 'lucide-react';
import type { Contact, TeamMember } from '../../types';
import { CONTACT_STATUSES, LEAD_SEGMENTS } from '../../constants';
import PipelineActions from './PipelineActions';

interface LeadTableProps {
  contacts: Contact[];
  members: TeamMember[];
  onEdit: (contact: Contact) => void;
  onChanged?: () => void;
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const getFollowUpClass = (dateStr: string | null): string => {
  if (!dateStr) return 'text-slate-500';
  const date = new Date(dateStr);
  const now = new Date();
  if (date < now) return 'text-red-400';
  const diffDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'text-amber-400';
  return 'text-slate-400';
};

const LeadTable: React.FC<LeadTableProps> = ({ contacts, members, onEdit, onChanged }) => {
  const getOwnerEmail = (userId: string | null) => {
    if (!userId) return '—';
    const member = members.find((m) => m.user_id === userId);
    return member?.email?.split('@')[0] || userId.slice(0, 8);
  };

  const getStatusConfig = (status: string) => {
    return CONTACT_STATUSES.find((s) => s.value === status) || { label: status, colour: 'bg-slate-500' };
  };

  if (contacts.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <User size={48} className="mx-auto mb-4 opacity-50" />
        <p>No leads found</p>
        <p className="text-sm mt-1">Create your first lead to get started</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Name</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Company</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Contact</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Segment</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Status</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Follow-up</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Owner</th>
            <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Actions</th>
          </tr>
        </thead>
        <tbody>
          {contacts.map((contact) => {
            const statusConfig = getStatusConfig(contact.status);
            return (
              <tr
                key={contact.id}
                className="border-b border-slate-800 hover:bg-slate-800/50 transition-colors cursor-pointer"
                onClick={() => onEdit(contact)}
              >
                <td className="py-3 px-4">
                  <div className="font-medium text-white">
                    {contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || '—'}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2 text-slate-300">
                    <Building2 size={14} className="text-slate-500" />
                    {contact.company || '—'}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="space-y-1">
                    {contact.email && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-400">
                        <Mail size={12} />
                        {contact.email}
                      </div>
                    )}
                    {contact.phone && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-400">
                        <Phone size={12} />
                        {contact.phone}
                      </div>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className="text-sm text-slate-300">
                    {LEAD_SEGMENTS.find((s) => s.value === contact.segment)?.label || contact.type}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${statusConfig.colour}`}>
                    {statusConfig.label}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className={`flex items-center gap-1.5 text-sm ${getFollowUpClass(contact.next_follow_up)}`}>
                    <Calendar size={14} />
                    {formatDate(contact.next_follow_up)}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className="text-sm text-slate-400">
                    {getOwnerEmail(contact.owner_user_id)}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit(contact); }}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <Edit2 size={16} />
                  </button>
                  {onChanged && <PipelineActions contact={contact} onChanged={onChanged} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default LeadTable;
